import React from "react";
import { ShoppingCart, PackageOpen, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CartItemCard from "./CartItemCard";
import type { CartItem } from "./types";

interface CartListProps {
  cartItems: CartItem[];
  cartLoading: boolean;
  enhancedLoading: boolean;
  onQuantityChange: (cartKey: string, quantity: number) => void;
  onRemoveItem: (cartKey: string) => void;
  isLoading: boolean;
}

const CartList: React.FC<CartListProps> = ({
  cartItems,
  cartLoading,
  enhancedLoading,
  onQuantityChange,
  onRemoveItem,
  isLoading,
}) => {
  const navigate = useNavigate();

  if (cartLoading) {
    return (
      <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden">
        <div className="p-6 border-b border-section-border">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-section-text">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Your Cart
          </h2>
        </div>
        <div className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <span className="text-section-muted font-medium">Loading your cart...</span>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden">
        <div className="p-6 border-b border-section-border">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-section-text">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Your Cart
          </h2>
        </div>
        <div className="p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-gold/10 rounded-full blur-xl opacity-50"></div>
              <PackageOpen className="relative w-24 h-24 text-section-muted mx-auto" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-section-text mb-3">Your cart is empty</h3>
            <p className="text-section-muted mb-8 leading-relaxed">
              Looks like you have not added anything to your cart yet. Start shopping to fill it up!
            </p>
            <button
              onClick={() => navigate("/products")}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-xl font-bold hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_4px_14px_rgba(201,169,110,0.2)]"
            >
              <ShoppingCart className="w-5 h-5" />
              Start Shopping
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden">
      <div className="p-6 border-b border-section-border">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-section-text">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Your Cart
          </h2>
          {enhancedLoading && (
            <div className="flex items-center gap-2 text-sm text-section-muted bg-section-surface px-4 py-2 rounded-full border border-section-border">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
              <span className="font-medium">Updating...</span>
            </div>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      {/* <div className="px-6 py-4 bg-section-surface border-b border-section-border">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shadow-sm mb-2 ring-4 ring-primary/10">
              1
            </div>
            <span className="text-xs font-semibold text-primary">Cart</span>
          </div>
          <div className="flex-1 h-px bg-section-border mx-2"></div>
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-section-card-bg border border-section-border text-section-muted flex items-center justify-center font-bold mb-2">
              2
            </div>
            <span className="text-xs font-semibold text-section-muted">Address</span>
          </div>
          <div className="flex-1 h-px bg-section-border mx-2"></div>
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-section-card-bg border border-section-border text-section-muted flex items-center justify-center font-bold mb-2">
              3
            </div>
            <span className="text-xs font-semibold text-section-muted">Payment</span>
          </div>
        </div>
      </div> */}

      <div className="p-6">
        <div className="space-y-5">
          {cartItems
            .slice()
            .reverse()
            .map((item) => (
              <CartItemCard
                key={item.id}
                item={item}
                onQuantityChange={onQuantityChange}
                onRemove={onRemoveItem}
                isLoading={isLoading}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default CartList;
